import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { Link } from "react-router-dom";
import { ContactForm } from "@/components/program/ContactForm";
import { LogoUpload } from "@/components/program/LogoUpload";
import { APIClient, ClientApi } from "@/api";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import countries from "@/constants/array/country_city.json";
import { PAYMENT_CURRENCY_OPTIONS } from "@/constants/program.constant";
import { LoaderButton } from "@/components/ui/loader-button";
import VirtualizedCitySelect from "@/components/ui/virtualized-city-select";

const TransferPartnerCreate = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: yupResolver(TransferPartnerSchema),
    defaultValues: {
      id: null,
      logo: null,
      partnerName: "",
      partnerCode: "",
      partnerDescription: "",
      address: "",
      country: "",
      city: "",
      loyaltyCurrency: "",
      redemptionCPP: "",
      clientId: null,
      paymentCurrency: "USD",
      pointsIncrement: "",
      vatNo: "",
      websiteUrl: "",
      contacts: [
        {
          name: "",
          email: "",
          countryCode: "+971",
          phoneNumber: "",
          role: "",
        },
      ],
    },
  });

  useEffect(() => {
    getClients();
  }, []);

  const getClients = async () => {
    try {
      const res = await ClientApi.getAllClients();
      setClients(
        (res?.data || [])?.map((client) => ({
          value: `${client?.id || ""}`,
          label: client?.clientName,
        }))
      );
    } catch (error) {
      toast.error(error?.message || "Failed to fetch clients");
    }
  };

  const onSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const { contacts, ...partnerData } = formData;
      const formDataToSend = new FormData();
      formDataToSend.append("transferPartnerLogo", formData?.logo);

      const currency = PAYMENT_CURRENCY_OPTIONS?.find(
        (currency) => currency?.value === formData?.paymentCurrency
      );

      const transferPartnerData = {
        id: formData?.id,
        partnerName: formData?.partnerName,
        partnerCode: formData?.partnerCode,
        businessDescription: formData?.partnerDescription,
        businessLocation: formData?.address,
        country: selectedCountry?.name,
        city: formData?.city,
        loyaltyCurrency: formData?.loyaltyCurrency,
        costPerPoint: formData?.redemptionCPP,
        clientId: formData?.clientId,
        currencyId: currency?.id,
        pointsIncrement: formData?.pointsIncrement,
        taxId: formData?.vatNo,
        websiteUrl: formData?.websiteUrl,
        createdDateInMillis: Date.now(),
        lastUpdatedDateInMillis: Date.now(),
      };

      const blob = new Blob([JSON.stringify(transferPartnerData)], {
        type: "application/json",
      });
      formDataToSend.append("transferPartnerVO", blob);

      // Create the transfer partner first
      const { data: partnerResponse } = await APIClient(
        "/transferPartner/createOrUpdateTransferPartner",
        formDataToSend,
        { requestType: "post", contentType: "multipart/form-data" }
      );

      const partnerId = partnerResponse?.id;
      form.setValue("id", partnerId);

      // Create contacts if partner was created successfully
      if (partnerId && contacts && contacts.length > 0) {
        const contactPromises = contacts.map(async (contact) => {
          const contactData = {
            contactName: contact.name,
            contactEmail: contact.email,
            contactRole: contact.role,
            phoneNumber: contact.phoneNumber,
            phoneCountryCode: contact.countryCode,
            partnerId: partnerId,
          };

          console.log(contactData, "contactData---");

          return APIClient(
            "/transferPartner/createOrUpdateTransferPartnerContact",
            contactData,
            { requestType: "post" }
          );
        });

        await Promise.all(contactPromises);
      }

      toast.success("Created successfully");
      navigate("/xpandpoint/transfer-programs");
      // navigate("/xpandpoint/transfer-partners");
    } catch (error) {
      toast.error(error?.message || "Failed to create transfer partner");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <Link to="/xpandpoint/transfer-programs">
            {/* <Link to="/xpandpoint/transfer-partners"> */}
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Create New Program
          </h1>
          <p className="text-gray-500">Create a new program</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* General Information */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Logo Upload */}
                <LogoUpload form={form} />

                <FormField
                  control={form.control}
                  name="partnerName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Emirates Skywards"
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value.replace(
                              /[^a-zA-Z0-9\s]/g,
                              ""
                            );
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        The name of your program
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="partnerCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Code*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. EMIR001"
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value.replace(
                              /[^a-zA-Z0-9]/g,
                              ""
                            );
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        A unique identifier for the program
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="partnerDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description*</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Describe the transfer partner..."
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Brief description of the program
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="clientId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client*</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Client" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {clients?.map((client) => (
                            <SelectItem
                              key={client?.value}
                              value={client?.value}
                            >
                              {client?.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="vatNo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>VAT Number</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. AE123456789" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="websiteUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website URL*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://www.example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        The official website of the transfer partner
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Location Information + Loyalty Details (compact) */}
            <div className="flex flex-col gap-0">
              <Card className="border-0 shadow-sm overflow-hidden bg-white mb-2">
                <CardHeader className="card-header-compact bg-gray-50">
                  <CardTitle className="text-lg font-medium text-gray-800">
                    Location Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address*</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter the complete address"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country*</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            const selected = JSON.parse(value);
                            if (selected?.name !== field.value) {
                              form.setValue("city", "", { shouldDirty: true });
                              form.clearErrors("city");
                            }
                            setSelectedCountry(selected);
                            field.onChange(selected.name);
                          }}
                          value={
                            field.value && Array.isArray(countries)
                              ? JSON.stringify(
                                  countries.find(
                                    (c: any) => c.name === field.value
                                  )
                                )
                              : ""
                          }
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select Country" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {(countries as any)?.map((country) => (
                              <SelectItem
                                key={country?.name}
                                value={JSON.stringify(country)}
                              >
                                {country?.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <VirtualizedCitySelect
                        cities={selectedCountry?.cities || []}
                        value={field.value || ""}
                        onValueChange={(value) => {
                          form.setValue("city", value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }}
                        placeholder="Select City"
                        disabled={!selectedCountry?.cities?.length}
                        label="City"
                        error={form.formState.errors.city?.message}
                        withForm={true}
                      />
                    )}
                  />
                </CardContent>
              </Card>

              {/* Loyalty Details - immediately below, compact */}
              <Card className="border-0 shadow-sm overflow-hidden bg-white">
                <CardHeader className="card-header-compact bg-gray-50">
                  <CardTitle className="text-lg font-medium text-gray-800">
                    Loyalty Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  <FormField
                    control={form.control}
                    name="loyaltyCurrency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Loyalty Currency*</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. Skywards Miles"
                            {...field}
                            onChange={(e) => {
                              const value = e.target.value.replace(
                                /[^a-zA-Z0-9\s]/g,
                                ""
                              );
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormDescription>
                          The name of the loyalty currency
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="redemptionCPP"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Redemption CPP*</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. 0.012"
                            {...field}
                            onChange={(e) => {
                              let value = e.target.value.replace(
                                /[^0-9.]/g,
                                ""
                              );
                              // Prevent multiple decimal points
                              const parts = value.split(".");
                              if (parts.length > 2) {
                                value =
                                  parts[0] + "." + parts.slice(1).join("");
                              }
                              // Prevent values greater than 100
                              const numValue = parseFloat(value);
                              if (numValue > 100) {
                                value = "100";
                              }
                              field.onChange(value);
                            }}
                            onKeyDown={(e) => {
                              // Prevent negative sign
                              if (e.key === "-") {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => {
                              // Prevent number input from changing value on scroll
                              e.currentTarget.blur();
                            }}
                          />
                        </FormControl>
                        <FormDescription>
                          Cost per point for redemption
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="paymentCurrency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payment Currency*</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select currency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {PAYMENT_CURRENCY_OPTIONS?.map((currency) => (
                              <SelectItem
                                key={currency?.id}
                                value={currency?.value}
                              >
                                {currency?.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="pointsIncrement"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Points Increment*</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. 100"
                            {...field}
                            onChange={(e) => {
                              let value = e.target.value.replace(
                                /[^0-9.]/g,
                                ""
                              );
                              // Prevent multiple decimal points
                              const parts = value.split(".");
                              if (parts.length > 2) {
                                value =
                                  parts[0] + "." + parts.slice(1).join("");
                              }
                              // Prevent leading zeros (except for decimal numbers like 0.5)
                              if (
                                value.length > 1 &&
                                value.startsWith("0") &&
                                !value.startsWith("0.")
                              ) {
                                value = value.replace(/^0+/, "");
                              }
                              // Prevent just "0" (but allow "0." for decimal input)
                              if (value === "0") {
                                value = "";
                              }
                              field.onChange(value);
                            }}
                            onKeyDown={(e) => {
                              // Prevent negative sign
                              if (e.key === "-") {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => {
                              // Prevent number input from changing value on scroll
                              e.currentTarget.blur();
                            }}
                          />
                        </FormControl>
                        <FormDescription>
                          The increment value for points
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contact Information */}
          <Card className="border-0 shadow-sm overflow-hidden bg-white">
            <CardHeader className="card-header-compact bg-gray-50">
              <CardTitle className="text-lg font-medium text-gray-800">
                Contact Information
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Add one or more contact persons for this transfer partner
              </p>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <ContactForm form={form} />
            </CardContent>
          </Card>

          <Separator className="my-8" />

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/xpandpoint/transfer-programs")}
              // onClick={() => navigate("/xpandpoint/transfer-partners")}
            >
              Cancel
            </Button>
            <LoaderButton type="submit" loading={isSubmitting}>
              <Save className="h-4 w-4 mr-2" /> Create Program
            </LoaderButton>
          </div>
        </form>
      </Form>
    </div>
  );
};

const TransferPartnerSchema = Yup.object().shape({
  id: Yup.string().optional().nullable(),
  logo: Yup.mixed().required("Logo is required"),
  partnerName: Yup.string()
    .required("Partner Name is required")
    .matches(
      /^[a-zA-Z0-9\s]+$/,
      "Partner name cannot contain special characters"
    ),
  partnerCode: Yup.string()
    .required("Partner Code is required")
    .matches(
      /^[a-zA-Z0-9]+$/,
      "Partner code cannot contain spaces or special characters"
    ),
  partnerDescription: Yup.string().required("Partner Description is required"),
  address: Yup.string().required("Address is required"),
  country: Yup.string().required("Country is required"),
  city: Yup.string().required("City is required"),
  loyaltyCurrency: Yup.string()
    .required("Loyalty Currency is required")
    .matches(
      /^[a-zA-Z0-9\s]+$/,
      "Loyalty currency cannot contain special characters"
    ),
  redemptionCPP: Yup.string()
    .required("Redemption CPP is required")
    .matches(/^\d+(\.\d+)?$/, "Redemption CPP must be a positive number")
    .test(
      "max-value",
      "Redemption CPP cannot be greater than 100",
      function (value) {
        if (!value) return true;
        const numValue = parseFloat(value);
        return numValue <= 100;
      }
    ),
  clientId: Yup.string().required("Client is required"),
  paymentCurrency: Yup.string().required("Payment Currency is required"),
  pointsIncrement: Yup.string()
    .required("Points Increment is required")
    .matches(
      /^[1-9]\d*(\.\d+)?$/,
      "Points increment must be a positive number greater than 0"
    ),
  vatNo: Yup.string(),
  websiteUrl: Yup.string()
    .required("Website URL is required")
    .url("Website URL must be a valid HTTP URL format"),
  contacts: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .required("Contact name is required")
        .matches(
          /^[A-Za-z\s]+$/,
          "Full name can only contain letters and spaces"
        ),
      email: Yup.string()
        .required("Email is required")
        .email("Email must be a valid email address"),
      role: Yup.string()
        .required("Role is required")
        .matches(/^[A-Za-z\s]+$/, "Role can only contain letters and spaces"),
      countryCode: Yup.string(),
      phoneNumber: Yup.string()
        .required("Phone is required")
        .matches(/^\d+$/, "Phone number can only contain positive numbers")
        .min(7, "Phone number must be at least 7 digits")
        .max(15, "Phone number cannot exceed 15 digits"),
    })
  ),
});

export default TransferPartnerCreate;
